import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from './ui/dialog';
import { Button } from './ui/button';
import { UserCog, Loader2, ArrowRight } from 'lucide-react';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

// Roles autorisés comme destinataires d'une réaffectation (hors prestataires)
const ASSIGNABLE_ROLES = ['commercial', 'commerciale', 'assistante_commerciale', 'admin', 'super_admin'];

export default function ReassignSimulationDialog({ sim, open, onOpenChange, onDone }) {
  const { token } = useAuth();
  const [users, setUsers] = useState([]);
  const [selected, setSelected] = useState('');
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    setSelected('');
    setLoading(true);
    fetch(`${API}/api/users/list`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => (r.ok ? r.json() : []))
      .then(data => setUsers((data || []).filter(u => ASSIGNABLE_ROLES.includes(u.role))))
      .catch(() => toast.error('Impossible de charger les collaborateurs'))
      .finally(() => setLoading(false));
  }, [open, token]);

  const handleReassign = async () => {
    if (!selected || !sim) return;
    setSaving(true);
    try {
      const r = await fetch(`${API}/api/simulations/${sim.id}/reassign`, {
        method: 'PATCH',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ new_user_id: selected }),
      });
      const d = await r.json();
      if (r.ok) {
        toast.success(d.message || 'Étude réaffectée');
        onOpenChange(false);
        onDone && onDone(d);
      } else {
        toast.error(d.detail || 'Échec de la réaffectation');
      }
    } catch {
      toast.error('Erreur réseau');
    } finally {
      setSaving(false);
    }
  };

  const roleLabel = (role) => ({
    commercial: 'Commercial', commerciale: 'Commerciale',
    assistante_commerciale: 'Assistante', admin: 'Admin', super_admin: 'Super Admin',
  }[role] || role);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md" data-testid="reassign-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <UserCog className="w-5 h-5 text-indigo-600" /> Réaffecter l'étude
          </DialogTitle>
          <DialogDescription>
            {sim ? (
              <>Attribuer « {sim.project_name || sim.retailer_name || 'cette étude'} » à un autre collaborateur. Actuellement : <strong>{sim.creator_name || 'Non attribué'}</strong>.</>
            ) : ''}
          </DialogDescription>
        </DialogHeader>

        <div className="py-2">
          {loading ? (
            <div className="flex items-center justify-center py-8 text-slate-400"><Loader2 className="w-6 h-6 animate-spin" /></div>
          ) : (
            <div className="space-y-2">
              <label className="text-xs font-medium text-slate-500">Nouveau collaborateur</label>
              <select
                value={selected}
                onChange={(e) => setSelected(e.target.value)}
                className="h-10 w-full rounded-lg border border-slate-200 bg-white px-3 text-sm text-slate-700 focus:outline-none focus:ring-2 focus:ring-indigo-200"
                data-testid="reassign-user-select"
              >
                <option value="">— Choisir un collaborateur —</option>
                {users.map(u => (
                  <option key={u.id} value={u.id}>{u.name || u.email} · {roleLabel(u.role)}</option>
                ))}
              </select>
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={saving}>Annuler</Button>
          <Button
            className="gap-2 bg-indigo-600 hover:bg-indigo-700"
            onClick={handleReassign}
            disabled={!selected || saving}
            data-testid="reassign-confirm-btn"
          >
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <ArrowRight className="w-4 h-4" />}
            Réaffecter
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
